import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './RemoteLogin.css';

/**
 * RemoteLogin Component
 * 
 * Allows users to sign in to their desktop app from a web browser
 * by entering a 6-character session code and signing with MetaMask
 */
function RemoteLogin({ onShowUserStatus }) {
  const [sessionCode, setSessionCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, loading, ready_to_sign, signing, success, error
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');

  // Get configuration from environment variables
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const webApiCode = process.env.REACT_APP_WEB_API_CODE || '';

  /**
   * Check for connected wallet on mount
   */
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (window.ethereum) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const accounts = await provider.listAccounts();
          if (accounts.length > 0) {
            const address = await accounts[0].getAddress();
            setWalletAddress(address);
          }
        } catch (err) {
          console.log('No wallet connected yet');
        }
      }
    };
    
    checkWalletConnection();
  }, []);

  /**
   * Listen for MetaMask account changes
   */
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        } else {
          setWalletAddress('');
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, []);

  /**
   * Check for session code in URL parameters on mount
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionParam = urlParams.get('session');
    
    if (sessionParam) {
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Validate and set the session code
      const cleanCode = sessionParam.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (cleanCode.length === 6) {
        setSessionCode(cleanCode);
        // Auto-submit the code
        handleCodeSubmitWithCode(cleanCode);
      } else {
        setError('Invalid session code in URL. Please enter a valid 6-character code.');
      }
    }
  }, []);

  /**
   * Handle session code input change
   * Automatically converts to uppercase
   */
  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setSessionCode(value);
    }
  };

  /**
   * Handle session code submission with a specific code
   * Used by URL parameter auto-submit
   */
  const handleCodeSubmitWithCode = async (code) => {
    try {
      setStatus('loading');
      setError('');
      
      // Validate session code format (6 uppercase alphanumeric)
      const codePattern = /^[A-Z0-9]{6}$/;
      if (!codePattern.test(code)) {
        setError('Session code must be 6 uppercase letters/numbers');
        setStatus('idle');
        return;
      }
      
      // Fetch session data from server
      const response = await fetch(`${apiUrl}/api/LoginSession?code=${webApiCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          FunctionArgument: {
            RequestType: 'GetLoginSession',
            session_code: code
          }
        })
      });
      
      const data = await response.json();
      
      // Handle different response statuses
      if (data.Reason === 'Error') {
        setError(data.Error || 'Invalid session code');
        setStatus('idle');
        return;
      }
      
      if (data.status === 'expired') {
        setError('Session has expired. Please request a new code from your desktop app.');
        setStatus('idle');
        return;
      }
      
      if (data.status === 'completed') {
        setError('This session has already been completed.');
        setStatus('idle');
        return;
      }
      
      if (data.status === 'pending' && data.login_challenge) {
        setChallenge(data.login_challenge);
        setExpiresAt(data.expires_at);
        setStatus('ready_to_sign');
      } else {
        setError('Unexpected response from server');
        setStatus('idle');
      }
    } catch (err) {
      setError('Failed to connect to server: ' + err.message);
      setStatus('idle');
    }
  };

  /**
   * Handle session code submission
   * Fetches the login challenge from the server
   */
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setStatus('loading');
      setError('');
      
      // Use the shared submission logic
      await handleCodeSubmitWithCode(sessionCode);
    } catch (err) {
      setError('Failed to connect to server: ' + err.message);
      setStatus('idle');
    }
  };

  /**
   * Handle wallet connection
   */
  const handleConnectWallet = async () => {
    try {
      if (!window.ethereum) {
        setError('MetaMask is not installed. Please install MetaMask to continue.');
        return;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      setWalletAddress(address);
      setError('');
    } catch (err) {
      if (err.code === 4001) {
        setError('Wallet connection was rejected');
      } else {
        setError('Failed to connect wallet: ' + err.message);
      }
    }
  };

  /**
   * Handle MetaMask signing
   * Signs the challenge and submits to server
   */
  const handleSign = async () => {
    try {
      setStatus('signing');
      setError('');
      
      // Check if MetaMask is installed
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
      }
      
      // Request MetaMask connection if not already connected
      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const walletAddress = await signer.getAddress();
      
      // Store wallet address in state if not already set
      if (!walletAddress) {
        setWalletAddress(walletAddress);
      }
      
      // Sign the challenge message
      const signature = await signer.signMessage(challenge);
      
      // Submit signature to server
      const response = await fetch(`${apiUrl}/api/LoginSession?code=${webApiCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          FunctionArgument: {
            RequestType: 'SubmitLoginSession',
            session_code: sessionCode,
            wallet_address: walletAddress,
            signature: signature
          }
        })
      });
      
      const result = await response.json();
      
      // Handle response
      if (result.Reason === 'Error') {
        setError(result.Error || 'Signature verification failed');
        setStatus('ready_to_sign');
        return;
      }
      
      if (result.status === 'completed') {
        setStatus('success');
      } else if (result.status === 'expired') {
        setError('Session expired during signing. Please try again.');
        setStatus('idle');
        setChallenge(null);
      } else {
        setError('Unexpected response from server');
        setStatus('ready_to_sign');
      }
    } catch (err) {
      if (err.code === 4001) {
        // User rejected the signature request
        setError('Signature request was rejected');
      } else {
        setError('Signing failed: ' + err.message);
      }
      setStatus('ready_to_sign');
    }
  };

  /**
   * Reset the form to start over
   */
  const handleReset = () => {
    setSessionCode('');
    setChallenge(null);
    setStatus('idle');
    setError('');
    setExpiresAt(null);
    setWalletAddress('');
  };

  return (
    <div className="remote-login-container">
      <div className="remote-login-card">
        {/* Wallet Status Header */}
        <div className="wallet-header">
          {walletAddress ? (
            <div className="wallet-connected">
              <span className="wallet-status-icon">🟢</span>
              <div className="wallet-info">
                <span className="wallet-status-label">Connected</span>
                <span className="wallet-status-address">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
            </div>
          ) : (
            <div className="wallet-disconnected">
              <span className="wallet-status-icon">⚪</span>
              <span className="wallet-status-label">Not Connected</span>
              <button onClick={handleConnectWallet} className="btn-connect-wallet">
                Connect Wallet
              </button>
            </div>
          )}
        </div>

        <h1>Login</h1>
        <p className="subtitle">
            Use this login page from almost anywhere on any device that supports MetaMask to sign in remotely.
        </p>

        {status === 'idle' && (
          <form onSubmit={handleCodeSubmit} className="code-form">
            <div className="form-group">
              <label htmlFor="sessionCode">Enter 6-digit code from your desktop app:</label>
              <input
                id="sessionCode"
                type="text"
                value={sessionCode}
                onChange={handleCodeChange}
                placeholder="ABC123"
                maxLength="6"
                className="code-input"
                autoFocus
              />
              <small className="help-text">
                The code is displayed on your desktop application
              </small>
            </div>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={sessionCode.length !== 6}
            >
              Continue
            </button>
          </form>
        )}

        {status === 'loading' && (
          <div className="status-message">
            <div className="spinner"></div>
            <p>Verifying session code...</p>
          </div>
        )}

        {status === 'ready_to_sign' && (
          <div className="sign-section">
            <div className="info-box">
              <p><strong><span className="success-check">✓</span> Session {sessionCode} found!</strong></p>
              {expiresAt && (
                <small className="expires-info">
                  Session expires at {new Date(expiresAt).toLocaleTimeString()}
                </small>
              )}
            </div>
            
            {walletAddress && (
              <div className="wallet-display">
                <span className="wallet-label">Connected Wallet:</span>
                <span className="wallet-address">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
            )}
            
            <div className="security-warning">
              <span className="warning-icon">⚠️</span>
              <strong>Security Notice:</strong> Please verify the message below before signing
            </div>
            
            <div className="challenge-display">
              <label className="challenge-label">Message you will sign:</label>
              <div className="challenge-text">
                {challenge}
              </div>
            </div>
            
            <button onClick={handleSign} className="btn btn-primary btn-large">
              Sign with MetaMask
            </button>
            <button onClick={handleReset} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        )}

        {status === 'signing' && (
          <div className="sign-section">
            <div className="status-message">
              <div className="spinner"></div>
              <p>Please check MetaMask and sign the message...</p>
              {walletAddress && (
                <div className="wallet-display">
                  <span className="wallet-label">Signing with:</span>
                  <span className="wallet-address">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
                </div>
              )}
            </div>
            
            <div className="challenge-display">
              <label className="challenge-label">Compare this message with MetaMask:</label>
              <div className="challenge-text">
                {challenge}
              </div>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="success-section">
            <div className="success-icon">✓</div>
            <h2>Login Successful!</h2>
            {walletAddress && (
              <div className="wallet-display success-wallet">
                <span className="wallet-label">Logged in with:</span>
                <span className="wallet-address">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
            )}
            <p>You can now return to your desktop application.</p>
            <p className="success-detail">Your desktop app should automatically log you in, and you may close this browser window once it does.</p>
            <button onClick={handleReset} className="btn btn-secondary">
              Sign in with another code
            </button>
          </div>
        )}

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {status !== 'success' && status !== 'idle' && (
          <button onClick={handleReset} className="btn btn-link">
            Start over
          </button>
        )}
      </div>

      <footer className="footer">
        <p>Make sure MetaMask is installed and unlocked | <a onClick={onShowUserStatus} className="status-inline-link">Check status</a></p>
        <div className="footer-links">
          <a href="/" className="home-link">← Home Page</a>
        </div>
        <div className="footer-legal">
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          {' | '}
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </div>
      </footer>
    </div>
  );
}

export default RemoteLogin;
